import { Box, Heading, Link, Stack } from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import {
  MachineTaskAnalyticsDTO,
  MachineType,
  MachineWorkItem,
  Measure,
} from "app/machines/models";
import { useTranslation } from "react-i18next";
import { MACHINES_NAMESPACE } from "../locales";
import moment from "moment";
import { DataTable } from "primereact/datatable";
import { tableStyle } from "app/shared/tables/style";
import { Column } from "primereact/column";
import { ClassNames } from "@emotion/react";
import {
  getDatePresentationValue,
  getPositionPresentationValue,
  getConsumableRefPresentationValue,
} from "../utils";
import { useStoreContext } from "store/context";
import { MachineProgress } from "./MachineProgress";
import { observer } from "mobx-react";
import {
  getMachineTaskDetails,
  getMachineInstanceSensorData,
} from "../services";
import { SensorGraph } from "./SensorGraph";

interface Props {
  machineType: MachineType;
  taskId: string;
  openConsumableDetails: (
    consumableRef: string,
    startDate: string,
    endDate: string
  ) => void;
}

const AnalyticTaskDetails: React.FC<Props> = ({
  machineType,
  taskId,
  openConsumableDetails,
}) => {
  const { t } = useTranslation(MACHINES_NAMESPACE);
  const { machineStore } = useStoreContext();
  const [analytics, setAnalytics] = useState<
    MachineTaskAnalyticsDTO | undefined
  >();
  const [sensorData, setSensorData] = useState<Measure[]>([]);
  const [sessionStartDate, setSessionStartDate] = useState<
    string | undefined
  >();
  const [sessionEndDate, setSessionEndDate] = useState<string | undefined>();

  useEffect(() => {
    getMachineTaskDetails(taskId).then((session) => {
      setAnalytics(session);

      const sessionStartDate = session.startDate;
      const sessionEndDate = moment(session.endDate)
        .utc()
        .subtract(30, "seconds")
        .toISOString();

      setSessionStartDate(sessionStartDate);
      setSessionEndDate(sessionEndDate);

      // Get machine data to build graph
      machineStore
        .getMachineInstance(session.machineInstanceId)
        .then(async (machineInstance) => {
          const sensorIds: string[] = [];

          machineInstance.machineDefinition?.telemetry
            ?.filter((it) =>
              [
                "WorkingArea",
                "WorkingStatus",
                "AutoPilot",
                "NumberOfWorkSessions",
                "TractionSpeed",
              ].includes(it.type)
            )
            ?.forEach((it) => sensorIds.push(it.name));

          if (sensorIds.length) {
            const data = await getMachineInstanceSensorData(
              machineInstance.id,
              {
                page: 0,
                size: 100,
                filters: [
                  { field: "sensors", values: sensorIds },
                  {
                    field: "startDate",
                    values: [sessionStartDate],
                  },
                  {
                    field: "endDate",
                    values: [sessionEndDate],
                  },
                  {
                    field: "rows",
                    values: ["1000"],
                  },
                ],
              }
            );
            const measures: Measure[] = [];
            data.forEach((a) =>
              a.measures.forEach((b) =>
                measures.push({
                  instanceId: a.instanceId,
                  name: a.name,
                  timestamp: b.timestamp,
                  value: b.value,
                })
              )
            );
            setSensorData(
              measures.sort((a, b) => a.timestamp.localeCompare(b.timestamp))
            );
          }
        });
    });
  }, [machineStore, taskId]);

  if (!analytics) {
    return null;
  }

  return (
    <Box>
      <Heading variant="h2" color="primary.100" textTransform="uppercase">
        {t(
          `analyticsDetails.${machineType === "Drill" ? "drillRef" : "cutRef"}`
        )}
        : {analytics.sessionNumber}
      </Heading>

      <Heading
        variant="h4"
        color="primary.100"
        textTransform="uppercase"
        mt={10}>
        {t("analyticsDetails.details")}
      </Heading>
      <Stack direction={["column", "row"]} spacing={[2, 4, 6]} mt={5}>
        <Box width={["100%", "50%"]}>
          <Heading variant="h7" textTransform="uppercase">
            {t("analyticsDetails.startDate")}
          </Heading>
          <Heading variant="h5">
            {moment(analytics.startDate).format("YYYY-MM-DD HH:mm")}
          </Heading>
          <Heading variant="h7" textTransform="uppercase" mt={3}>
            {t("analyticsDetails.totalWorkingTime")}
          </Heading>
          <Heading variant="h5">
            {getDatePresentationValue(analytics.workHours)}
          </Heading>
          <Heading variant="h7" textTransform="uppercase" mt={3}>
            {t(
              `analyticsDetails.${
                machineType === "Drill"
                  ? "totalDrillingArea"
                  : "totalCuttingArea"
              }`
            )}
          </Heading>
          <Heading variant="h5">
            {t("analyticsDetails.workingAreaValue", {
              value: analytics.workSize,
              unit: t(
                `analyticsDetails.${
                  machineType === "Drill" ? "meters" : "squareMeters"
                }`
              ),
            })}
          </Heading>
          {machineType === "Wire" && (
            <>
              <Heading variant="h7" textTransform="uppercase" mt={3}>
                {t("analyticsDetails.position")}
              </Heading>
              <Heading variant="h5">
                {getPositionPresentationValue(
                  analytics.cutType,
                  analytics.cutPosition
                )}
              </Heading>
            </>
          )}
          {machineType === "Drill" && (
            <>
              <Heading variant="h7" textTransform="uppercase" mt={3}>
                {t("analyticsDetails.totalGroups")}
              </Heading>
              <Heading variant="h5">{analytics.totalGroups}</Heading>
            </>
          )}
        </Box>
        <Box width={["100%", "50%"]}>
          <Heading variant="h7" textTransform="uppercase">
            {t("analyticsDetails.endDate")}
          </Heading>
          <Heading variant="h5">
            {moment(analytics.endDate).format("YYYY-MM-DD HH:mm")}
          </Heading>
          <Heading variant="h7" textTransform="uppercase" mt={3}>
            {t("analyticsDetails.machineInstanceId")}
          </Heading>
          <Heading variant="h5">{analytics.machineInstanceId}</Heading>
          <Heading variant="h7" textTransform="uppercase" mt={3}>
            {t(
              `analyticsDetails.${
                machineType === "Drill" ? "totalParts" : "totalWires"
              }`
            )}
          </Heading>
          <Heading variant="h5">{analytics.totalConsumables}</Heading>
          <Heading variant="h7" textTransform="uppercase" mt={3}>
            {t(
              `analyticsDetails.${
                machineType === "Drill"
                  ? "averageDrillingSpeed"
                  : "averageCuttingSpeed"
              }`
            )}
          </Heading>
          <Heading variant="h5">
            {t("analyticsDetails.averageSpeedValue", {
              value: analytics.averageSpeed,
              unit: t(
                `analyticsDetails.${
                  machineType === "Drill" ? "meters" : "squareMeters"
                }`
              ),
            })}
          </Heading>
        </Box>
      </Stack>

      <Heading
        variant="h4"
        color="primary.100"
        textTransform="uppercase"
        mt={5}
        mb={5}>
        {t(
          `analyticsDetails.${
            machineType === "Drill" ? "drillHistory" : "cutHistory"
          }`
        )}
      </Heading>
      <ClassNames>
        {({ css, cx }) => (
          <DataTable
            value={analytics.workItems.filter(
              (it) => it.workSize > 0 || it.workHours > 0 || it.averageSpeed > 0
            )}
            className={css`
              ${tableStyle}
            `}>
            <Column
              header={t(
                `analyticsDetails.${
                  machineType === "Drill" ? "partRef" : "wireRef"
                }`
              )}
              field="consumableRef"
              body={(workItem: MachineWorkItem) =>
                workItem.consumableRef ? (
                  <Link
                    onClick={() =>
                      openConsumableDetails(
                        workItem.consumableRef!!,
                        workItem.startDate,
                        workItem.endDate
                      )
                    }>
                    {getConsumableRefPresentationValue(workItem.consumableRef)}
                  </Link>
                ) : (
                  "-"
                )
              }
              sortable={true}
            />
            {machineType === "Drill" && (
              <Column
                header={t("analyticsDetails.group")}
                field="group.name"
                body={(workItem: MachineWorkItem) =>
                  workItem.group?.name ?? "-"
                }
                sortable={true}
              />
            )}
            <Column
              header={t(
                `analyticsDetails.${
                  machineType === "Drill" ? "meters" : "squareMeters"
                }`
              )}
              field="workSize"
              body={(workItem: MachineWorkItem) =>
                t("analyticsDetails.workingAreaValue", {
                  value: workItem.workSize,
                  unit: t(
                    `analyticsDetails.${
                      machineType === "Drill" ? "meters" : "squareMeters"
                    }`
                  ),
                })
              }
              sortable={true}
            />
            <Column
              header={t("analyticsDetails.workHours")}
              field="workHours"
              body={(workItem: MachineWorkItem) =>
                getDatePresentationValue(workItem.workHours)
              }
              sortable={true}
            />
            <Column
              header={t("analyticsDetails.averageSpeed")}
              field="averageSpeed"
              body={(workItem: MachineWorkItem) =>
                t("analyticsDetails.averageSpeedValue", {
                  value: workItem.averageSpeed,
                  unit: t(
                    `analyticsDetails.${
                      machineType === "Drill" ? "meters" : "squareMeters"
                    }`
                  ),
                })
              }
              sortable={true}
            />
            <Column
              header={t("analyticsDetails.startDate")}
              field="startDate"
              body={(workItem: MachineWorkItem) =>
                moment(workItem.startDate).format("YYYY-MM-DD HH:mm")
              }
              sortable={true}
            />
            <Column
              header={t("analyticsDetails.endDate")}
              field="endDate"
              body={(workItem: MachineWorkItem) =>
                moment(workItem.endDate).format("YYYY-MM-DD HH:mm")
              }
              sortable={true}
            />
          </DataTable>
        )}
      </ClassNames>

      {sensorData.length > 0 && (
        <>
          <Heading
            variant="h4"
            color="primary.100"
            textTransform="uppercase"
            mt={5}
            mb={5}>
            {t("analyticsDetails.graphs")}
          </Heading>
          {machineStore.machineInstance?.configuration
            ?.taskProgressFunction && (
            <Box mb={5}>
              <MachineProgress
                machineFunctionBody={
                  machineStore.machineInstance?.configuration
                    ?.taskProgressFunction
                }
                measures={sensorData}
                endDate={sessionEndDate}
              />
            </Box>
          )}
          {machineStore.machineInstance?.machineDefinition?.telemetry
            ?.filter((it) => ["TractionSpeed"].includes(it.type))
            ?.map((telemetry, key) => (
              <SensorGraph
                key={key}
                graph={{
                  label: `${telemetry.label} (${telemetry.unit})`,
                  lastMinutes: -1,
                  fullWidth: true,
                  graphType: "Graph",
                  dataSources: [
                    {
                      sensorId: telemetry.name,
                      color: "#DC0000",
                    },
                  ],
                }}
                telemetry={[telemetry]}
                definition={machineStore.machineInstance?.machineDefinition!!}
                measures={sensorData.filter(
                  (sensor) => sensor.name === telemetry.name
                )}
                showLastValue={false}
                firstTimestamp={sessionStartDate}
                lastTimestamp={sessionEndDate!!}
                lineType="stepAfter"
              />
            ))}
        </>
      )}
    </Box>
  );
};

export default observer(AnalyticTaskDetails);
